// @ts-strict-ignore
/**
 * Breakdown menu: multi-screen menu to select event+prop pair
 */
import * as React from 'react';

import {renameEvent} from 'common/report/util/events';
import {propagateEvent} from 'common/util/dom';
import {PATH_SEPARATOR} from 'common/constants';
import {truncateMiddle} from 'common/util/string';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';
import {Segmentation} from 'common/types/reports/query';
import {Translatable} from 'common/i18n';

import {ChildMenuInterface} from '../child-menu';
import type {EventItem} from '../events-menu/types';
import type {PropertySectionItem} from '../properties-menu/item-menu-types';
import {EventsMenuReact} from '../events-menu';
import {PropertiesMenuReact} from '../properties-menu';
import {MAX_LABEL_LENGTH} from '../constants';
import {IncludeCustomPropertyType, Property, SelectedPropertyItem} from '../properties-menu/types';
import {isListOfObjectsSegment} from '../segment-menu/types';
import {propertySelectionToSegmentation} from '../segment-menu/util';

interface SelectedEventProperty {
  event: EventItem;
  property: PropertySectionItem;
}

export interface SelectedEventPropertyItem {
  item: SelectedEventProperty;
}

interface BreakdownMenuState {
  staged: any;
}

interface BreakdownMenuProps {
  listProperty?: Segmentation;
  dataGroupId?: string;
  open?: boolean;
  projectId?: number;
  selected?: SelectedEventProperty;
  showSpecialDatePropertyUnit?: boolean;
  workspaceId?: number;

  // Flags plumbed for properties-menu
  includeCustomProperties: IncludeCustomPropertyType;
  showBorrowedProperties: boolean;

  // More attrs plumbed for properties-menu
  // Unclear how much of this is actually needed
  allDataGroups?: boolean;
  excludeDimensionProperties?: boolean;
  excludeLookupTableProperties?: boolean;
  hideAllPropertiesSelect?: boolean;
  hideEditControls?: boolean;
  hideEvents?: boolean;
  inlineCohortsOverride?: {enable: boolean};
  multiple?: boolean;
  projectHasCohorts?: boolean;
  projectHasPeople?: boolean;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  supportB2bCohorts?: boolean;

  // Below attributes read by ChildMenu methods
  eventNames?: string[];
  readonly?: boolean;
  trackName?: string;

  // Event handlers
  onSelect: (ev: any) => void;

  onRender?: () => void;
}

export class BreakdownMenu
  extends React.PureComponent<BreakdownMenuProps, BreakdownMenuState>
  implements ChildMenuInterface
{
  protected declare childMenu: ChildMenuInterface;

  state = {
    staged: null,
  };

  static defaultProps = {
    includeCustomProperties: IncludeCustomPropertyType.None,
  };

  componentDidMount() {
    this.setState({staged: this.props.selected});
  }

  componentDidUpdate(prevProps: BreakdownMenuProps, _prevState): void {
    const isSelectedChanged = this.props.selected !== prevProps.selected;
    const isMenuClosing = !this.props.open && prevProps.open;
    if (isSelectedChanged || isMenuClosing) {
      this.setState({staged: this.props.selected});
    }

    this.props.onRender?.();
  }

  propagateEvent = (ev) => propagateEvent(this, ev);

  handleSelectProperty = (item: SelectedPropertyItem<Property>) => {
    let property = item.item;
    const segmentation = propertySelectionToSegmentation(property) as Segmentation;
    const currSegmentation =
      this.state.staged.property && (propertySelectionToSegmentation(this.state.staged.property) as Segmentation);

    if (currSegmentation && isListOfObjectsSegment(currSegmentation)) {
      property = {...this.state.staged.property, listItemGroup: segmentation};
    }

    // don't emit event if we still need to pick a prop for the list of objects
    if (!isListOfObjectsSegment(segmentation) || property?.listItemGroup) {
      this.props.onSelect(
        new CustomEvent<SelectedEventPropertyItem>(`select`, {
          detail: {
            item: {
              event: this.getSelectedEvent(),
              property: property as PropertySectionItem,
            },
          },
        }),
      );
    }
    this.setState({staged: {...this.state.staged, property}});
  };

  handleSelectEvent(event: EventItem) {
    this.setState({staged: {event}});
  }

  public getSelectedProperty() {
    return this.state.staged?.property || null;
  }

  getSelectedHoverTooltipData() {
    return this.childMenu?.getSelectedHoverTooltipData();
  }

  handleTooltipContextAction(ev: Event) {
    return this.childMenu?.handleTooltipContextAction?.(ev);
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged?.event;
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  canOpenMenu() {
    return !this.props.readonly;
  }

  shouldCloseOnSelect() {
    return true;
  }

  getPrefixLabel() {
    let label: string = null;
    const selected = this.getSelectedEvent();
    const eventName = this.getSelectedEventName();
    let evName = renameEvent(eventName);

    if (evName) {
      /** @todo [pablo] find a better workaround for Insights using properties-menu for people mode */
      if (selected.value === `$all_people`) {
        evName = selected.label ?? eventName;
      }

      label = truncateMiddle(evName, MAX_LABEL_LENGTH) + PATH_SEPARATOR; // event name + right arrow
    }

    return label;
  }

  selectedLabel() {
    let label: Translatable = ``;
    const propName = getSelectedPropertyName({
      selected: this.getSelectedProperty(),
      showSpecialDatePropertyUnit: this.props.showSpecialDatePropertyUnit,
    });

    if (!this.getSelectedEvent()?.value) {
      label = ({t}) => t(`query-builder.select-event`);
    } else {
      label = propName || (({t}) => t(`query-builder.select-property`));
    }

    return label;
  }

  render() {
    const selectedEvent = this.getSelectedEvent();
    const segmentation =
      this.state.staged?.property && (propertySelectionToSegmentation(this.state.staged.property) as Segmentation);
    let menuEl;
    if (selectedEvent?.value) {
      menuEl = (
        <PropertiesMenuReact
          key='properties-menu'
          context='breakdown'
          ref={(el) => (this.childMenu = el as ChildMenuInterface)}
          listProperty={isListOfObjectsSegment(segmentation) ? segmentation : null}
          eventNames={[selectedEvent.value]}
          selected={this.getSelectedProperty()}
          allDataGroups={false}
          projectHasPeople={false}
          projectHasCohorts={false}
          includeCustomProperties={this.props.includeCustomProperties}
          showBorrowedProperties={this.props.showBorrowedProperties}
          excludeDimensionProperties={this.props.excludeDimensionProperties}
          excludeLookupTableProperties={this.props.excludeLookupTableProperties}
          dataGroupId={this.props.dataGroupId}
          hideAllPropertiesSelect={this.props.hideAllPropertiesSelect}
          hideEditControls={this.props.hideEditControls}
          hideEvents={this.props.hideEvents}
          inlineCohortsOverride={this.props.inlineCohortsOverride}
          multiple={this.props.multiple}
          open={this.props.open}
          projectId={this.props.projectId}
          readonly={this.props.readonly}
          showAllEvents={this.props.showAllEvents}
          showAnyEvent={this.props.showAnyEvent}
          showSessionEvent={this.props.showSessionEvent}
          showSpecialDatasetProperty={this.props.showSpecialDatasetProperty}
          showSpecialDatePropertyUnit={this.props.showSpecialDatePropertyUnit}
          showTopEvents={this.props.showTopEvents}
          supportB2bCohorts={this.props.supportB2bCohorts}
          trackName={this.props.trackName}
          workspaceId={this.props.workspaceId}
          onSelect={this.handleSelectProperty}
          onTooltipContextEditDescription={this.propagateEvent}
        />
      );
    } else {
      menuEl = (
        <EventsMenuReact
          showSessionEvent
          ref={(el) => (this.childMenu = el as ChildMenuInterface)}
          open={this.props.open}
          dataGroupId={this.props.dataGroupId}
          projectId={this.props.projectId}
          workspaceId={this.props.workspaceId}
          selected={this.getSelectedEvent()}
          onSelect={(ev: CustomEvent) => this.handleSelectEvent(ev.detail.item)}
          key='events-menu'
        />
      );
    }
    return <div className='mp-breakdown-menu'>{menuEl}</div>;
  }
}

export const BreakdownMenuReact = BreakdownMenu;
